import React, { useCallback, useEffect, useMemo, useState } from "react";
import Button from "@civicplus/preamble-ui/lib/Button";
import Grid from "@civicplus/preamble-ui/lib/Grid";
import Link from "@civicplus/preamble-ui/lib/Link";
import Loader from "@civicplus/preamble-ui/lib/Loader";
import SearchInput from "@civicplus/preamble-ui/lib/SearchInput";
import Table from "@civicplus/preamble-ui/lib/Table";
import Titlebar from "@civicplus/preamble-ui/lib/Titlebar";
import Typography from "@civicplus/preamble-ui/lib/Typography";
import { bottle } from "../../../provider/Bottle";
import { format } from "date-fns";
import { Link as RouterLink } from "react-router";
import { OrganizationService } from "../../../services/OrganizationService";
import { SourceLabels, Suppression } from "../../../entities/Suppression";
import { SuppressionsService } from "../../../services/SuppressionsService";
import { MessageService } from "../../../services/MessageService";
import { EmailEvent } from "../../../entities/Message";
import { useSnackbar } from "notistack";
import Dialog from "@civicplus/preamble-ui/lib/Dialog";
import CodeEditor from "@civicplus/preamble-ui/lib/CodeEditor";

export const UserActivity: React.FC = () => {
    const [userEmail, setUserEmail] = useState<string | null>(null);
    const [suppressions, setSuppressions] = useState<Suppression[]>([]);
    const [emailEvents, setEmailEvents] = useState<Map<string, EmailEvent[]>>(new Map<string, EmailEvent[]>());
    const [emailCount, setEmailCount] = useState<Map<string, number>>(new Map<string, number>());
    const noEmailEvents = useMemo(() => {
        let noEvents = true;
        emailEvents.forEach((events) => {
            if (events.length > 0) {
                noEvents = false;
            }
        });
        return noEvents;
    }, [emailEvents]);
    const [scrollToTop, setScrollToTop] = useState<boolean>(false);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState<Map<string, number>>(new Map<string, number>());
    const [rowsPerPage, setRowsPerPage] = useState<Map<string, number>>(new Map<string, number>());
    const [openMessageDialog, setOpenMessageDialog] = useState<boolean>(false);
    const [messageData, setMessageData] = useState<EmailEvent | null>(null);

    const orgService: OrganizationService = bottle.container.OrganizationService;
    const suppressionsService: SuppressionsService = bottle.container.SuppressionsService;
    const messageService: MessageService = bottle.container.MessageService;
    const shardNames = bottle.container.ConfigOptions.shardNames;
    const { enqueueSnackbar } = useSnackbar();

    const handleDeleteSuppressions = async () => {
        if (userEmail) {
            try {
                await suppressionsService.deleteSuppressions(userEmail);
                enqueueSnackbar("Suppression successfully removed.", { variant: "success" });
                const suppressions = await suppressionsService.getSuppressions(userEmail);
                setSuppressions(suppressions);
            } catch (e) {
                enqueueSnackbar("Failed to remove suppressions. ", { variant: "error" });
            }
        }
    };

    const changePage = async (newPage: number, shardName: string) => {
        setPage((currentMap) => {
            currentMap.set(shardName, newPage);
            return new Map(currentMap);
        });

        const events = await getEmailEventsForSingleShard(
            userEmail,
            shardName,
            newPage,
            rowsPerPage.get(shardName) ?? 10
        );

        setEmailEvents((currentMap) => {
            currentMap.set(shardName, events.items);
            return new Map(currentMap);
        });

        setEmailCount((currentMap) => {
            currentMap.set(shardName, events.count);
            return new Map(currentMap);
        });
    };

    const changeRowsPerPage = async (rows: number, shardName: string) => {
        setPage((currentMap) => {
            currentMap.set(shardName, 0);
            return new Map(currentMap);
        });

        setRowsPerPage((currentMap) => {
            currentMap.set(shardName, rows);
            return new Map(currentMap);
        });

        const events = await getEmailEventsForSingleShard(userEmail, shardName, 0, rows);

        setEmailEvents((currentMap) => {
            currentMap.set(shardName, events.items);
            return new Map(currentMap);
        });

        setEmailCount((currentMap) => {
            currentMap.set(shardName, events.count);
            return new Map(currentMap);
        });
    };

    useEffect(() => {
        if (!loading && scrollToTop) {
            window.scroll(0, 0);
        }
    }, [rowsPerPage, loading, scrollToTop, emailEvents]);

    const getEmailEventsForSingleShard = useCallback(
        async (email: string | null, shardName: string, page: number, rowsPerPage: number) => {
            if (!email) return { items: [], count: 0 };

            const emailEvents = await messageService.getMessageEventsForEmail(
                email,
                rowsPerPage,
                page * rowsPerPage,
                shardName
            );
            return emailEvents;
        },
        [messageService]
    );

    const getEmailEvents = useCallback(
        async (email: string | null) => {
            if (email) {
                const events = new Map<string, EmailEvent[]>();
                const eventCounts = new Map<string, number>();

                for (const shardName of shardNames) {
                    const emailEvents = await getEmailEventsForSingleShard(email, shardName, 0, 10);
                    events.set(shardName, emailEvents.items);
                    eventCounts.set(shardName, emailEvents.count);
                }

                setEmailEvents(events);
                setEmailCount(eventCounts);
            }
        },
        [getEmailEventsForSingleShard, shardNames]
    );

    const findByEmail = useCallback(
        async (email: string, scrollToTop = false) => {
            try {
                setSuppressions([]);
                setEmailEvents(new Map());
                setEmailCount(new Map());

                setUserEmail(email);
                setLoading(true);
                const suppressions = await suppressionsService.getSuppressions(email);
                setSuppressions(suppressions);

                await getEmailEvents(email);
                setScrollToTop(scrollToTop);
            } finally {
                setLoading(false);
            }
        },
        [suppressionsService, getEmailEvents]
    );

    const handleActivityEventClick = async (
        _: React.MouseEvent<HTMLTableRowElement>,
        row: EmailEvent,
        shardName: string
    ) => {
        setOpenMessageDialog(true);
        const messageId = row.messageId;
        const typeId = row.type;
        const messageActivity = await messageService.getMessageEventsData(messageId, typeId, shardName);
        if (messageActivity.length > 0) {
            setMessageData(messageActivity[0]);
        }
    };

    useEffect(() => {
        getEmailEvents(userEmail);
        setScrollToTop(true);
    }, [getEmailEvents, userEmail]);

    return (
        <>
            <Titlebar
                id="titlebar"
                title="User Activity"
                description="Search for a user's email to find email events and suppressions."
                breadcrumbs={[
                    <Link key="admin" id="admin" to={`/${orgService.apiService._orgId}/admin`} component={RouterLink}>
                        Administration
                    </Link>
                ]}
            />

            <SearchInput
                id="user-lookup"
                placeholder="Enter email address..."
                onSearch={findByEmail}
                searchOnChange={false}
                fullWidth={true}
                margin={false}
                sx={{ my: 2 }}
            />

            {userEmail !== null && loading === true && <Loader sx={{ my: 2 }} />}

            {!loading && userEmail !== null && (
                <Grid container={true}>
                    {noEmailEvents ? (
                        <Grid xs={12}>
                            <Typography variant="h5" title="Email Events">
                                Email Events
                            </Typography>
                            <Table
                                id={`email-events-table`}
                                emptyState="No message history for this user."
                                pagination={{
                                    count: 0,
                                    page: 0,
                                    rowsPerPage: 10,
                                    rowsPerPageOptions: [10, 25, 50],
                                    onPageChange: (event, page) => {},
                                    onRowsPerPageChange: (event) => {}
                                }}
                                columns={[
                                    {
                                        columnHeader: "Id",
                                        rowProperty: "id"
                                    },
                                    {
                                        columnHeader: "Subject",
                                        rowProperty: "subject"
                                    },
                                    {
                                        columnHeader: "Type",
                                        rowProperty: "type"
                                    },
                                    {
                                        columnHeader: "Time",
                                        rowProperty: "time"
                                    },
                                    {
                                        columnHeader: "Organization Id",
                                        rowProperty: "organization"
                                    },
                                    {
                                        columnHeader: "Client Id",
                                        rowProperty: "client"
                                    }
                                ]}
                                rows={[]}
                            />
                        </Grid>
                    ) : (
                        <></>
                    )}
                    {Array.from(emailEvents.entries()).map(([shardName, events]) =>
                        events.length === 0 ? (
                            <></>
                        ) : (
                            <Grid xs={12} key={shardName}>
                                <Typography variant="h5" title="Email Events">
                                    Email Events for{" "}
                                    {shardName.toLowerCase() === "notifications"
                                        ? "Primary Database"
                                        : "Database " + shardName.toLowerCase().replace("shard", "")}
                                </Typography>
                                <Table
                                    id={`email-events-table-${shardName}`}
                                    data-testid={`email-events-table-${shardName}`}
                                    emptyState="No message history for this user."
                                    pagination={{
                                        count: emailCount.get(shardName) ?? 0,
                                        page: page.get(shardName) ?? 0,
                                        rowsPerPage: rowsPerPage.get(shardName) ?? 10,
                                        rowsPerPageOptions: [10, 25, 50],
                                        onPageChange: (event, page) => changePage(page, shardName),
                                        onRowsPerPageChange: (event) =>
                                            changeRowsPerPage(Number(event.target.value), shardName)
                                    }}
                                    columns={[
                                        {
                                            columnHeader: "Id",
                                            rowProperty: "id"
                                        },
                                        {
                                            columnHeader: "Subject",
                                            rowProperty: "subject"
                                        },
                                        {
                                            columnHeader: "Type",
                                            rowProperty: "type"
                                        },
                                        {
                                            columnHeader: "Time",
                                            rowProperty: "time"
                                        },
                                        {
                                            columnHeader: "Organization Id",
                                            rowProperty: "organization"
                                        },
                                        {
                                            columnHeader: "Client Id",
                                            rowProperty: "client"
                                        }
                                    ]}
                                    rows={events.map((item) => {
                                        return {
                                            ...item,
                                            time: format(new Date(`${item.time.toString()}Z`), "MM/dd/yyyy hh:mm aaaa")
                                        };
                                    })}
                                    onRowClick={async (_: React.MouseEvent<HTMLTableRowElement>, row: EmailEvent) => {
                                        await handleActivityEventClick(_, row, shardName);
                                    }}
                                />
                            </Grid>
                        )
                    )}

                    <Grid xs={12}>
                        <Typography variant="h5" title="Email Suppressions">
                            Email Suppressions
                        </Typography>

                        <Table
                            role="table"
                            data-testid="suppression-table"
                            id="suppression-table"
                            emptyState="No suppressions found"
                            columns={[
                                {
                                    columnHeader: "Email",
                                    rowProperty: "to"
                                },
                                {
                                    columnHeader: "Source",
                                    rowProperty: "source"
                                },
                                {
                                    columnHeader: "Description",
                                    rowProperty: "description"
                                },
                                {
                                    columnHeader: "Code",
                                    rowProperty: "code"
                                },
                                {
                                    columnHeader: "Time",
                                    rowProperty: "time"
                                }
                            ]}
                            rows={suppressions?.map((item) => {
                                return {
                                    to: item.to,
                                    description: item.description,
                                    source: SourceLabels[item.source],
                                    code: item.errorCode,
                                    time: format(
                                        new Date(item.time.endsWith("Z") ? item.time : `${item.time}Z`),
                                        "MM/dd/yyyy hh:mm aaaa"
                                    )
                                };
                            })}
                        />

                        <Dialog
                            title="Message Log Details"
                            fullWidth={true}
                            maxWidth="lg"
                            open={openMessageDialog}
                            onClose={() => {
                                setOpenMessageDialog(false);
                                setMessageData(null);
                            }}
                        >
                            {messageData ? (
                                messageData.type === "email_queued" || !messageData.data ? (
                                    <div>
                                        <Typography variant="h6">No additional details.</Typography>
                                    </div>
                                ) : (
                                    <CodeEditor
                                        language="json"
                                        value={JSON.stringify(JSON.parse(messageData.data), null, 2)}
                                        readOnly={true}
                                    />
                                )
                            ) : (
                                <Loader sx={{ my: 2 }} />
                            )}
                        </Dialog>
                    </Grid>
                    {(suppressions?.length ?? 0) > 0 && (
                        <Grid xs={12} sx={{ mt: 2 }}>
                            <Button color="primary" onClick={handleDeleteSuppressions}>
                                Delete Suppressions
                            </Button>
                        </Grid>
                    )}
                </Grid>
            )}
        </>
    );
};
